import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { parseTimelineDate, formatTimelineMeta } from "../../utils/dateUtils";
import { formatLocation } from "../../services/geoService";
import { escapeCsv } from "../../utils/formatters";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

function cleanEventUrl(url) {
  if (!url) return "—";
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    return u.hostname + path;
  } catch { return url; }
}

const NOISE_SEGMENTS = new Set(["sandbox","modern","custom","legacy","web-pixel","web-pixels"]);

function humanizePath(url) {
  if (!url) return "—";
  try {
    const segments = new URL(url).pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "Homepage";
    const meaningful = segments.filter(s =>
      !s.includes("@") &&
      !/^[a-f0-9]{16,}$/i.test(s) &&
      !/^[A-Za-z0-9]{16,}$/.test(s) &&
      !/^[a-z]{2}(-[a-z]{2,3})?$/i.test(s) &&
      !NOISE_SEGMENTS.has(s.toLowerCase())
    );
    return (meaningful.length > 0 ? meaningful : segments.slice(-1))
      .map(s => s.replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase()))
      .join(" \u00B7 ");
  } catch { return "—"; }
}

function extractPageUrl(raw) {
  if (!raw) return null;
  try {
    const m = typeof raw === "string" ? JSON.parse(raw) : raw;
    const candidates = [m.referrer, m.headers?.referer].filter(Boolean);
    return candidates.reduce((best, url) => {
      try {
        const bestDepth = best ? new URL(best).pathname.split("/").filter(Boolean).length : -1;
        const depth = new URL(url).pathname.split("/").filter(Boolean).length;
        return depth > bestDepth ? url : best;
      } catch { return best; }
    }, null);
  } catch { return null; }
}

export function CustomerTimelineModal({ customer, onClose }) {
  const { effectiveAdvertiserId: advertiserId } = useAuth();
  const motion = useModalMotion(onClose, { variant: "drawer", closeOnBackdrop: true });
  const { requestClose } = motion;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ impressions:0, siteVisits:0, purchases:0, eventType:"Purchase", journeyDurationDays:null, timeToConvertDays:null, roas:null });

  const assistedConversionId = customer?.assistedConversionId ?? null;

  function exportJourney() {
    const visitorId = customer?.visitor_id ?? customer?.customer ?? "unknown";
    const orderId   = customer?.id ?? assistedConversionId ?? "unknown";

    // ── summary header rows ───────────────────────────────────────────────
    const lines = [
      `"Customer Journey Export"`,
      `"Visitor ID","${String(visitorId).replace(/"/g, '""')}"`,
      `"Order / Conversion ID","=""${String(orderId).replace(/"/g, '""')}"""`,
      `"Conversion date",${escapeCsv(String(customer?.date ?? ""))}`,
      `"Revenue",${escapeCsv(String(customer?.revenue ?? ""))}`,
      `"ROAS","${summary.roas != null ? summary.roas.toFixed(2) : ""}"`,
      `"Journey duration (days)","${summary.journeyDurationDays ?? ""}"`,
      `"Time to convert (days)","${summary.timeToConvertDays ?? ""}"`,
      `"Total impressions",${summary.impressions}`,
      `"Total site visits",${summary.siteVisits}`,
      `"Total purchases",${summary.purchases}`,
      `""`,
      // column headers
      `"Step","Event Type","Label","Detail","Date & Time","Location","Additional Info"`,
    ];

    // ── one row per event ─────────────────────────────────────────────────
    events.forEach((ev, i) => {
      const typeLabel =
        ev.type === "ad_heard"   ? "Ad Heard"   :
        ev.type === "site_visit" ? "Site Visit"  :
        ev.type === "purchase"   ? "Purchase"    : ev.type;

      lines.push(
        [
          i + 1,
          escapeCsv(typeLabel),
          escapeCsv(ev.label   ?? ""),
          escapeCsv(ev.detail  ?? ""),
          escapeCsv(ev.meta    ?? ""),
          escapeCsv(ev.meta?.split("\u00B7").slice(1).join("\u00B7").trim() ?? ""),
          escapeCsv(ev.sub     ?? ""),
        ].join(",")
      );
    });

    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `journey_${String(visitorId).slice(0, 12)}_${String(orderId).slice(0, 8)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (!supabase || advertiserId == null || !assistedConversionId) {
      setEvents([]);
      setSummary({ impressions:0, siteVisits:0, purchases:0, journeyDurationDays:null, timeToConvertDays:null, roas:null });
      setLoading(false);
      setError(!assistedConversionId ? "No conversion selected" : advertiserId == null ? "No advertiser" : "Supabase not configured");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const rawEvents = [];
      let firstTs = null;
      let lastConversionTs = null;
      try {
        const { data: convRow, error: convErr } = await supabase
          .from("assisted_conversions")
          .select("id, event_id, event_type, event_value, created_at, event_timestamp, country, region, city, visitor_id")
          .eq("id", assistedConversionId)
          .single();
        if (cancelled) return;
        if (convErr || !convRow) { setError(convErr?.message || "Conversion not found"); setEvents([]); setLoading(false); return; }

        const conversionVisitorId = convRow.visitor_id ?? null;
        const conversionId = convRow.id;

        const { data: impressionsRows, error: impErr } = await supabase
          .from("assisted_conversion_impressions")
          .select("id, created_at, bid_time_utc, start_time_utc, city, region, country, dma, asset_name, content_series_name, content_episode_name, publisher_name, supply_partner_name, app_name, audio_feed_type, audio_q1, audio_q2, audio_q3, audio_complete")
          .eq("assisted_conversion_id", conversionId)
          .eq("advertiser_id", advertiserId);
        if (cancelled) return;
        if (impErr) { setError(impErr.message); setEvents([]); setLoading(false); return; }

        const sortedImpressions = (impressionsRows || []).slice().sort((a, b) => {
          const ta = parseTimelineDate(a.start_time_utc || a.bid_time_utc || a.created_at)?.getTime() ?? 0;
          const tb = parseTimelineDate(b.start_time_utc || b.bid_time_utc || b.created_at)?.getTime() ?? 0;
          return ta - tb;
        });
        sortedImpressions.forEach((r, idx) => {
          const at = parseTimelineDate(r.start_time_utc || r.bid_time_utc || r.created_at);
          if (at && !firstTs) firstTs = at;
          rawEvents.push({
            type:"ad_heard", sortAt: at ? at.getTime() : Date.now(), index: idx+1,
            podcast: r.content_series_name || r.publisher_name || r.app_name || "Not Provided",
            episode: r.content_episode_name, creative: r.asset_name || "—",
            audioComplete: r.audio_complete, audioQuartiles: [r.audio_q1, r.audio_q2, r.audio_q3].filter(Boolean).length,
            meta: at ? formatTimelineMeta(at) : "—", location: formatLocation(r) || "—",
          });
        });

        if (conversionVisitorId) {
          const { data: webRows, error: webErr } = await supabase
            .from("assisted_conversions_user_web_activity")
            .select("id, created_at, event_timestamp, event_url, url, type, city, region, country, dma, metadata, session_id")
            .eq("visitor_id", conversionVisitorId)
            .eq("advertiser_id", advertiserId)
            .lte("created_at", convRow.created_at)
            .order("created_at", { ascending:true });
          if (cancelled) return;
          if (!webErr && webRows?.length) {
            webRows.forEach((r) => {
              const at = parseTimelineDate(r.event_timestamp || r.created_at);
              if (at && !firstTs) firstTs = at;
              rawEvents.push({
                type:"site_visit", sortAt: at ? at.getTime() : Date.now(),
                url: r.url || null, eventType: r.type || "Page view", metadata: r.metadata || "",
                visitorId: r.visitor_id || (() => { try { const m = typeof r.metadata === "string" ? JSON.parse(r.metadata) : r.metadata; return m?.visitor_id || null; } catch { return null; } })(),
                meta: at ? formatTimelineMeta(at) : "—", location: formatLocation(r) || "—",
              });
            });
          }
        }

        // event_timestamp is the canonical conversion time (DEV-1873), but it
        // is still a nullable text column until DEV-1872 lands — fall back to
        // created_at so this display-only timeline never loses its sort key or
        // duration metrics. Matches the web-activity rows above.
        const convAt = parseTimelineDate(convRow.event_timestamp || convRow.created_at);
        if (convAt) { lastConversionTs = convAt; if (!firstTs) firstTs = convAt; }
        rawEvents.push({
          type:"purchase", sortAt: convAt ? convAt.getTime() : Date.now(),
          eventId: convRow.event_id || `AC-${convRow.id}`, eventType: convRow.event_type || "Conversion",
          eventValue: convRow.event_value, meta: convAt ? formatTimelineMeta(convAt) : "—",
          // Purchases carry no DMA, as in the Conversion Detail Report.
          location: formatLocation({ ...convRow, dma: null }) || "—",
        });

        rawEvents.sort((a, b) => a.sortAt - b.sortAt);

        const impressionCount = rawEvents.filter((e) => e.type === "ad_heard").length;
        const siteSessionCount = new Set(rawEvents.filter((e) => e.type === "site_visit" && e.visitorId).map((e) => e.visitorId)).size;
        const purchaseCount = rawEvents.filter((e) => e.type === "purchase").length;
        let journeyDurationDays = null;
        if (firstTs && lastConversionTs && purchaseCount > 0) {
          journeyDurationDays = Math.round((lastConversionTs - firstTs) / (24*60*60*1000));
        }
        const lastImpressionEv = rawEvents.filter((e) => e.type === "ad_heard").slice(-1)[0];
        const lastImpressionTs = lastImpressionEv?.sortAt ?? null;
        let timeToConvertDays = null;
        if (lastImpressionTs != null && lastConversionTs != null && purchaseCount > 0) {
          timeToConvertDays = Math.round((lastConversionTs - lastImpressionTs) / (24*60*60*1000));
        }

        if (cancelled) return;
        setSummary({ impressions:impressionCount, siteVisits:siteSessionCount, purchases:purchaseCount, eventType: convRow.event_type || "Purchase", journeyDurationDays, timeToConvertDays, roas:null });

        const typeColors = {
          ad_heard:   { bar:"#EEF2FF", dot:"#323084", icon:"🎙️", iconBg:"#EEF2FF", iconColor:"#323084" },
          site_visit: { bar:"#F5F0FF", dot:"#7C3AED", icon:"🌐", iconBg:"#F5F0FF", iconColor:"#7C3AED" },
          purchase:   { bar:"#d8fffa", dot:"#208a3e", icon:"✓", iconBg:"#d8fffa", iconColor:"#208a3e" },
        };

        const built = rawEvents.map((e) => {
          const c = typeColors[e.type];
          if (e.type === "ad_heard") {
            const parts = [e.creative, e.podcast];
            if (e.episode) parts.push(e.episode);
            const detail = parts.filter(Boolean).join(" \u00B7 ");
            const sub = e.audioComplete ? "Full listen-through" : (e.audioQuartiles > 0 ? `Listened through Q${e.audioQuartiles}` : "—");
            return { type:"ad_heard", icon:c.icon, iconBg:c.iconBg, iconColor:c.iconColor, label:`Heard Ad (Impression ${e.index})`, detail: detail || "—", meta: e.meta + (e.location ? ` \u00B7 ${e.location}` : ""), sub: sub || "—", connected:true };
          }
          if (e.type === "site_visit") {
            const pageUrl = e.url || extractPageUrl(e.metadata);
            const pageName = humanizePath(pageUrl);
            return { type:"site_visit", icon:c.icon, iconBg:c.iconBg, iconColor:c.iconColor, label: e.eventType || "Visited Website", detail:pageName, meta: e.meta + (e.location ? ` \u00B7 ${e.location}` : ""), sub: cleanEventUrl(pageUrl), connected:true };
          }
          const rev = e.eventValue != null ? `$${Number(e.eventValue).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}` : "";
          return { type:"purchase", icon:c.icon, iconBg:c.iconBg, iconColor:c.iconColor, label: e.eventType || "Purchase Completed", detail: rev ? `${e.eventId} \u00B7 ${rev}` : (e.eventId || "Conversion"), meta: e.meta + (e.location ? ` \u00B7 ${e.location}` : ""), sub: journeyDurationDays != null ? `${journeyDurationDays} days from first touch to conversion` : "—", connected:false };
        });

        setEvents(built);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load timeline");
        setEvents([]);
      }
      if (cancelled) return;
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [assistedConversionId, advertiserId]);

  const typeColors = {
    ad_heard:   { bar:"#EEF2FF", dot:"#323084" },
    site_visit: { bar:"#F5F0FF", dot:"#7C3AED" },
    purchase:   { bar:"#d8fffa", dot:"#208a3e" },
  };

  const dateRangeLabel = (() => {
    if (events.length === 0) return "No events";
    const first = events[0];
    const last = events[events.length - 1];
    const start = first?.meta?.split(" \u00B7 ")[0] || "";
    const end = last?.meta?.split(" \u00B7 ")[0] || "";
    return start && end ? `Full Journey \u00B7 ${start} \u2013 ${end}` : "Full Journey";
  })();

  return (
    <ModalShell motion={motion} labelledBy="customer-timeline-title" width={520} panelStyle={{ overflowY:"auto", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"24px 28px 20px", borderBottom:"1px solid #F0F0F0", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:11, color:"#7b7b7b", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Customer Journey</div>
              <div id="customer-timeline-title" style={{ fontSize:20, fontWeight:600, color:"#151515", fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>{customer?.customer ?? "—"}</div>
              <div style={{ fontSize:11, color:"#7b7b7b", letterSpacing:1, textTransform:"uppercase", marginTop:4 }}>{dateRangeLabel}</div>
            </div>
            <button onClick={requestClose} aria-label="Close" style={{ background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer", lineHeight:1 }}>✕</button>
          </div>
          <div style={{ display:"flex", gap:10, marginTop:18, flexWrap:"wrap" }}>
            {[
              { label:`${summary.impressions} Ad Impression${summary.impressions !== 1 ? "s" : ""}`, color:"#323084", bg:"#EEF2FF" },
              { label:`${summary.siteVisits} Site Visit${summary.siteVisits !== 1 ? "s" : ""}`, color:"#7C3AED", bg:"#F5F0FF" },
              { label:`${summary.purchases} ${(summary.eventType || "Purchase").replace(/\b\w/g, c => c.toUpperCase())}${summary.purchases !== 1 ? "s" : ""}`, color:"#208a3e", bg:"#d8fffa" },
            ].map(ch => (
              <div key={ch.label} style={{ padding:"4px 12px", borderRadius:20, background:ch.bg, color:ch.color, fontSize:11, fontWeight:600 }}>{ch.label}</div>
            ))}
          </div>
          <div style={{ marginTop:12, padding:"10px 14px", background:"#FAFAFA", borderRadius:10, border:"1px solid #E2E2E2" }}>
            <div>
              <span style={{ fontSize:12, color:"#7b7b7b" }}>Customer Journey Duration: </span>
              <span style={{ fontSize:12, color:"#151515", fontWeight:600 }}>{summary.journeyDurationDays != null ? `${summary.journeyDurationDays} days` : "—"}</span>
              <span style={{ fontSize:12, color:"#7b7b7b" }}> {'\u00B7'} First touch to purchase</span>
            </div>
            <div style={{ marginTop:6 }}>
              <span style={{ fontSize:12, color:"#7b7b7b" }}>Time to convert: </span>
              <span style={{ fontSize:12, color:"#151515", fontWeight:600 }}>{summary.timeToConvertDays != null ? `${summary.timeToConvertDays} days` : "—"}</span>
              <span style={{ fontSize:12, color:"#7b7b7b" }}> {'\u00B7'} Last impression to purchase</span>
            </div>
            {summary.roas != null && <div style={{ marginTop:6 }}><span style={{ fontSize:12, color:"#208a3e", fontWeight:600 }}>{summary.roas}× ROAS</span></div>}
          </div>
        </div>

        <div style={{ padding:"28px 28px", flex:1 }}>
          {loading && <div style={{ color:"#7b7b7b", fontSize:13 }}>Loading timeline…</div>}
          {error && <div style={{ color:"#e53935", fontSize:13, marginBottom:16 }}>{error}</div>}
          <div style={{ position:"relative" }}>
            <div style={{ position:"absolute", left:19, top:20, bottom:20, width:2, background:"#E2E2E2", borderRadius:2 }} />
            {!loading && events.map((ev, i) => (
              <div key={i} style={{ display:"flex", gap:18, marginBottom: i < events.length - 1 ? 28 : 0, position:"relative", minWidth:0 }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:ev.iconBg, border:`2px solid ${typeColors[ev.type].dot}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0, position:"relative", zIndex:1 }}>
                  <span style={{ color:ev.iconColor, fontSize: ev.type==="purchase" ? 16 : 14, fontWeight:700 }}>{ev.icon}</span>
                </div>
                <div style={{ flex:1, minWidth:0, overflow:"hidden", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:12, padding:"14px 16px", marginTop:4, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                  <div style={{ height:2, background: typeColors[ev.type].dot + "33", borderRadius:2, marginBottom:12 }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:6 }}>
                    <div style={{ fontWeight:600, color:"#151515", fontSize:13, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ev.label}</div>
                    <div style={{ fontSize:10, color: typeColors[ev.type].dot, background: typeColors[ev.type].bar, padding:"2px 8px", borderRadius:20, fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>
                      {ev.type === "ad_heard" ? "AD" : ev.type === "site_visit" ? "WEB" : (ev.label || "PURCHASE").toUpperCase()}
                    </div>
                  </div>
                  <div style={{ fontSize:12, color:"#626262", marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ev.detail}</div>
                  <div style={{ fontSize:11, color:"#7b7b7b", marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ev.meta}</div>
                  <div style={{ fontSize:11, color:"#7b7b7b", background:"#FAFAFA", borderRadius:6, padding:"6px 10px", wordBreak:"break-all", overflowWrap:"anywhere" }}>{ev.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding:"16px 28px", borderTop:"1px solid #F0F0F0", background:"#FAFAFA", flexShrink:0 }}>
          <div style={{ display:"flex", gap:10 }}>
            <button
              onClick={exportJourney}
              disabled={events.length === 0}
              style={{ flex:1, padding:"9px 0", background: events.length > 0 ? "#F90091" : "transparent", color: events.length > 0 ? "#fff" : "#B8B8B8", border:`1px solid ${events.length > 0 ? "#F90091" : "#E2E2E2"}`, borderRadius:8, fontSize:12, fontWeight: events.length > 0 ? 600 : 400, cursor: events.length > 0 ? "pointer" : "default", transition:"all 0.15s" }}
            >Export Journey</button>
            <button onClick={requestClose} style={{ flex:1, padding:"9px 0", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, cursor:"pointer" }}>Close</button>
          </div>
        </div>
    </ModalShell>
  );
}
